import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Box, Card, CardContent, Grid, IconButton, InputAdornment, Link, TextField, Typography } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { withRouter } from 'react-router-dom';
import Seo from '../Seo/Seo';

const styles = theme => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'radial-gradient(circle at top, #1c2c2f 0%, #0f1116 45%, #0a0c10 100%)',
    padding: theme.spacing(4),
  },
  card: {
    textAlign: 'left',
    background: 'rgba(20, 24, 28, 0.86)',
    color: '#f5f7fa',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 24,
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 24px 70px rgba(0,0,0,0.45)',
    backdropFilter: 'blur(16px)',
    padding: theme.spacing(1),
  },
  header: {
    fontWeight: 700,
    letterSpacing: '-0.02em',
  },
  subheader: {
    color: 'rgba(245, 247, 250, 0.7)',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  formStack: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  buttonRow: {
    display: 'grid',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  login: {
    width: '100%',
    '&:hover:not($disabled):not($cssFocused):not($error) $notchedOutline': {
      borderColor: 'rgba(120, 255, 200, 0.7)',
    },
  },
  input: {
    color: '#f5f7fa',
  },
  cssLabel: {
    color: 'rgba(245, 247, 250, 0.7)',
    '&$cssFocused': {
      color: '#72f7c4',
    },
  },
  cssOutlinedInput: {
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    borderRadius: 14,
    '&$cssFocused $notchedOutline': {
      borderColor: '#72f7c4',
      borderWidth: 2,
    },
  },
  cssFocused: {},
  notchedOutline: { borderColor: 'rgba(255,255,255,0.16)' },
  h1: {
    color: '#72f7c4',
    fontWeight: 700,
  },
  button: {
    padding: theme.spacing(1.4, 0),
    fontWeight: 600,
    borderRadius: 14,
    background: 'linear-gradient(135deg, #72f7c4 0%, #4ad6ff 100%)',
    color: '#0a0c10',
    textTransform: 'none',
    boxShadow: '0 10px 30px rgba(74, 214, 255, 0.35)',
    '&:hover': {
      background: 'linear-gradient(135deg, #8bffd2 0%, #6fe0ff 100%)',
    },
  },
  secondaryButton: {
    padding: theme.spacing(1.2, 0),
    fontWeight: 600,
    borderRadius: 14,
    border: '1px solid rgba(114, 247, 196, 0.5)',
    color: '#e7fff4',
    textTransform: 'none',
  },
  cardContent: {
    padding: theme.spacing(4),
  },
  forgotLink: {
    color: '#9cefd2',
    justifySelf: 'end',
    marginTop: theme.spacing(-0.5),
  },
  icon: {
    color: '#d0fced',
  },
});

class LoginPage extends Component {

  state = {
    username: '',
    password: '',
    showPassword: false,
  };

  componentDidMount() {
    if (this.props.user && this.props.user.id) {
      this.props.history.replace('/home');
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.user.id && this.props.user && this.props.user.id) {
      this.props.history.replace('/home');
    }
  }

  login = (event) => {
    event.preventDefault();
    console.log('you are hitting the button');

    if (this.state.username && this.state.password) {
      this.props.dispatch({
        type: 'LOGIN',
        payload: {
          username: this.state.username,
          password: this.state.password,
        },
      });
    } else {
      this.props.dispatch({ type: 'LOGIN_INPUT_ERROR' });
    }
  } // end login

  handleInputChangeFor = propertyName => (event) => {
    this.setState({
      [propertyName]: event.target.value,
    });
  }

  handleClickShowPassword = () => {
    this.setState((prevState) => ({
      showPassword: !prevState.showPassword,
    }));
  }

  handleMouseDownPassword = (event) => {
    event.preventDefault();
  }

  render() {

    const { classes } = this.props

    return (
      <Box className={classes.root}>
        <Seo
          title="Log In | Trivia Weekend"
          description="Log in to Trivia Weekend to manage your trivia team, track live rounds, and coordinate answers."
          canonicalPath="/#/login"
          ogType="website"
        />
        <Grid container justify="center">
          <Grid item xs={12} sm={9} md={6} lg={4}>
            <Card className={classes.card}>
              <CardContent className={classes.cardContent}>
                {this.props.errors.loginMessage && (
                  <h2
                    className="alert"
                    role="alert"
                  >
                    {this.props.errors.loginMessage}
                  </h2>
                )}
                <form onSubmit={this.login} className={classes.formStack}>
                  <div>
                    <Typography variant="h4" component="h1" className={classes.h1}>
                      Trivia Weekend
                    </Typography>
                    <Typography variant="subtitle1" className={classes.subheader}>
                      Welcome back! Sign in for some trivia business.
                    </Typography>
                  </div>
                  <TextField
                    align="left"
                    id="outlined-name"
                    label="Email Address"
                    className={classes.login}
                    value={this.state.username}
                    onChange={this.handleInputChangeFor('username')}
                    margin="normal"
                    variant="outlined"
                    InputProps={{
                      className: classes.input,
                      classes: {
                        root: classes.cssOutlinedInput,
                        focused: classes.cssFocused,
                        notchedOutline: classes.notchedOutline,
                      }
                    }}
                    InputLabelProps={{
                      className: classes.cssLabel,
                      shrink: true
                    }}
                  />
                  <TextField
                    type={this.state.showPassword ? 'text' : 'password'}
                    align="left"
                    id="outlined-password"
                    label="Password"
                    className={classes.login}
                    value={this.state.password}
                    onChange={this.handleInputChangeFor('password')}
                    margin="normal"
                    variant="outlined"
                    InputProps={{
                      className: classes.input,
                      classes: {
                        root: classes.cssOutlinedInput,
                        focused: classes.cssFocused,
                        notchedOutline: classes.notchedOutline,
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            className={classes.icon}
                            onClick={this.handleClickShowPassword}
                            onMouseDown={this.handleMouseDownPassword}
                            edge="end"
                          >
                            {this.state.showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    InputLabelProps={{
                      className: classes.cssLabel,
                      shrink: true
                    }}
                  />
                  <Link
                    component="button"
                    type="button"
                    className={classes.forgotLink}
                    onClick={() => this.props.history.push('/forgot-password')}
                  >
                    Forgot password?
                  </Link>
                  <div className={classes.buttonRow}>
                    <Button className={classes.button} type="submit" name="submit" value="Login">
                      Login
                    </Button>
                    <Button
                      className={classes.secondaryButton}
                      variant="outlined"
                      onClick={() => this.props.history.push('/register')}
                    >
                      Register
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }
}

// Instead of taking everything from state, we just want the error messages.
// if you wanted you could write this code like this:
// const mapStateToProps = ({errors}) => ({ errors });
const mapStateToProps = state => ({
  errors: state.errors,
  user: state.user,
});

export default withRouter(connect(mapStateToProps)(withStyles(styles)(LoginPage)));
